import { downloadCsv } from "@/lib/csv";
import { formatFullDate } from "@/lib/format";
import type { WebhookDelivery, WebhookEndpoint } from "@/pages/admin/approvals/hooks/useWebhooks";

// 端点健康快照的时间窗口：回调面板与报表中心共用同一套口径。
export type HealthWindow = "24h" | "7d" | "30d";

export const HEALTH_WINDOWS: HealthWindow[] = ["24h", "7d", "30d"];

export function healthWindowMs(window: HealthWindow): number {
  if (window === "24h") return 86400000;
  if (window === "30d") return 2592000000;
  return 604800000;
}

export interface WebhookHealthRow {
  endpointId: number;
  endpointName: string;
  total: number;
  success: number;
  failed: number;
  retrying: number;
  rate: number | null;
  avg: number | null;
  lastStatus: string | null;
  lastTime: string | null;
}

// 汇总每个接收端在所选时间窗口内的成功率 / 平均响应耗时 / 失败数 / 最近一次投递，
// 并按稳定性排序（成功率越低越靠前），方便一眼找出最不稳定的接收端。
export function computeWebhookHealth(
  endpoints: WebhookEndpoint[],
  deliveries: WebhookDelivery[],
  window: HealthWindow,
): WebhookHealthRow[] {
  const cutoff = Date.now() - healthWindowMs(window);
  const map = new Map<
    number,
    {
      total: number;
      success: number;
      failed: number;
      retrying: number;
      latencySum: number;
      latencyCount: number;
      lastAt: number;
      lastStatus: string | null;
    }
  >();

  deliveries.forEach((delivery) => {
    if (delivery.endpoint_id == null) return;
    if (new Date(delivery.created_at).getTime() < cutoff) return;
    const entry =
      map.get(delivery.endpoint_id) ??
      { total: 0, success: 0, failed: 0, retrying: 0, latencySum: 0, latencyCount: 0, lastAt: 0, lastStatus: null };
    entry.total += 1;
    if (delivery.status === "success") entry.success += 1;
    else if (delivery.status === "failed") entry.failed += 1;
    else if (delivery.status === "retrying") entry.retrying += 1;
    if (typeof delivery.duration_ms === "number") {
      entry.latencySum += delivery.duration_ms;
      entry.latencyCount += 1;
    }
    const created = new Date(delivery.created_at).getTime();
    if (created > entry.lastAt) {
      entry.lastAt = created;
      entry.lastStatus = delivery.status;
    }
    map.set(delivery.endpoint_id, entry);
  });

  return endpoints
    .map((endpoint) => {
      const stat = map.get(endpoint.id);
      const total = stat?.total ?? 0;
      const success = stat?.success ?? 0;
      const rate = total > 0 ? Math.round((success / total) * 100) : null;
      const avg = stat && stat.latencyCount > 0 ? Math.round(stat.latencySum / stat.latencyCount) : null;
      return {
        endpointId: endpoint.id,
        endpointName: endpoint.name,
        total,
        success,
        failed: stat?.failed ?? 0,
        retrying: stat?.retrying ?? 0,
        rate,
        avg,
        lastStatus: stat?.lastStatus ?? null,
        lastTime: stat && stat.lastAt > 0 ? new Date(stat.lastAt).toISOString() : null,
      };
    })
    .sort((a, b) => {
      const ra = a.rate ?? 101;
      const rb = b.rate ?? 101;
      if (ra !== rb) return ra - rb;
      return b.failed - a.failed;
    });
}

export interface WebhookHealthLabels {
  endpoint: string;
  rate: string;
  latency: string;
  total: string;
  failed: string;
  last: string;
  lastTime: string;
  fileName: string;
  statusLabel: (status: string) => string;
}

// 把当前时间窗口下的端点健康快照导出为 CSV，返回导出的行数（供写导出留痕）。
// 只负责产出文件，是否写留痕由调用方决定（回调面板与报表中心各自处理）。
export function downloadWebhookHealthSnapshot(
  endpoints: WebhookEndpoint[],
  deliveries: WebhookDelivery[],
  window: HealthWindow,
  labels: WebhookHealthLabels,
): number {
  const rows = computeWebhookHealth(endpoints, deliveries, window);
  downloadCsv(
    `${labels.fileName}-${window}.csv`,
    [
      { key: "endpoint", label: labels.endpoint, value: (row) => row.endpointName },
      { key: "rate", label: labels.rate, value: (row) => (row.rate == null ? "" : `${row.rate}%`) },
      { key: "avg", label: labels.latency, value: (row) => (row.avg == null ? "" : row.avg) },
      { key: "total", label: labels.total, value: (row) => row.total },
      { key: "failed", label: labels.failed, value: (row) => row.failed },
      { key: "last", label: labels.last, value: (row) => (row.lastStatus ? labels.statusLabel(row.lastStatus) : "") },
      { key: "lastTime", label: labels.lastTime, value: (row) => (row.lastTime ? formatFullDate(row.lastTime) : "") },
    ],
    rows,
  );
  return rows.length;
}